import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, ChevronRight, Download, Shield, Ship, DollarSign, FileCheck, X } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';
import { NavBar } from '@/components/NavBar';
import api from '@/lib/api';

const PRODUCTS = [
  { id: 'electronics', label: 'Electronics and components' },
  { id: 'dairy', label: 'Dairy and food ingredients' },
  { id: 'minerals', label: 'Critical minerals and metals' },
  { id: 'textiles', label: 'Textiles and apparel' },
  { id: 'machinery', label: 'Machinery and industrial equipment' },
  { id: 'chemicals', label: 'Chemicals' },
  { id: 'pharmaceuticals', label: 'Pharmaceuticals' },
  { id: 'automotive', label: 'Automotive parts' },
  { id: 'renewables', label: 'Renewable energy components' },
];

const RISK_COLORS = { low: '#2E7D32', moderate: '#FF9500', elevated: '#FF3B30' };

const SourcingPage = () => {
  const [countries, setCountries] = useState([]);
  const [product, setProduct] = useState('electronics');
  const [currentCountry, setCurrentCountry] = useState('CN');
  const [weights, setWeights] = useState({
    geo: [5], cost: [5], trade: [5], logistics: [5], esg: [5], maturity: [5],
  });
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [compareModal, setCompareModal] = useState(null);

  useEffect(() => {
    api.getCountries().then(res => setCountries(res.data || [])).catch(console.error);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const generate = async () => {
    setLoading(true);
    try {
      const res = await api.getSourcingAlternatives({
        product, current_country: currentCountry,
        geo_weight: weights.geo[0], cost_weight: weights.cost[0],
        trade_weight: weights.trade[0], logistics_weight: weights.logistics[0],
        esg_weight: weights.esg[0], maturity_weight: weights.maturity[0],
      });
      setResults(res.data);
    } catch (e) { console.error(e); } finally { setLoading(false); }
  };

  const handleDownloadPdf = () => {
    const params = {
      product, current_country: currentCountry,
      geo_weight: weights.geo[0], cost_weight: weights.cost[0],
      trade_weight: weights.trade[0], logistics_weight: weights.logistics[0],
      esg_weight: weights.esg[0], maturity_weight: weights.maturity[0],
    };
    window.open(api.downloadSourcingPdf(params), '_blank');
  };

  const riskLevel = (score) => score > 60 ? 'elevated' : score > 40 ? 'moderate' : 'low';
  const riskColor = (score) => RISK_COLORS[riskLevel(score)];

  return (
    <div className="min-h-screen bg-[#F5F5F5]">
      <NavBar />
      <main className="pt-24 px-4 lg:px-6 pb-8">
        <div className="max-w-[1200px] mx-auto">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mb-6">
            <h1 className="text-3xl font-bold text-[#0D3B4F]" data-testid="sourcing-page-title">Sourcing alternatives</h1>
            <p className="text-[#4E6974] mt-2">Evaluate alternative source countries by risk, cost, trade agreements, and logistics.</p>
          </motion.div>

          {/* Input section */}
          <Card className="bg-white rounded-lg shadow-[0_4px_24px_rgba(0,0,0,0.04)] border-none mb-6">
            <CardHeader className="pb-2"><CardTitle className="text-base font-bold text-[#0D3B4F]">Find sourcing alternatives</CardTitle></CardHeader>
            <CardContent className="space-y-5">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-medium text-[#4E6974] uppercase tracking-wide mb-1.5 block">What are you sourcing?</label>
                  <select value={product} onChange={e => setProduct(e.target.value)}
                    className="w-full border border-[#E0E0E0] rounded-lg px-3 py-2.5 text-sm text-[#0D3B4F] bg-white focus:border-[#0097A7] focus:ring-1 focus:ring-[#0097A7] outline-none" data-testid="sourcing-product-select">
                    {PRODUCTS.map(p => <option key={p.id} value={p.id}>{p.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-medium text-[#4E6974] uppercase tracking-wide mb-1.5 block">Where are you currently sourcing from?</label>
                  <select value={currentCountry} onChange={e => setCurrentCountry(e.target.value)}
                    className="w-full border border-[#E0E0E0] rounded-lg px-3 py-2.5 text-sm text-[#0D3B4F] bg-white focus:border-[#0097A7] focus:ring-1 focus:ring-[#0097A7] outline-none" data-testid="sourcing-country-select">
                    <option value="">Exploring new sourcing (no current source)</option>
                    {countries.sort((a, b) => a.country_name.localeCompare(b.country_name)).map(c => (
                      <option key={c.country_code} value={c.country_code}>{c.country_name}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="text-xs font-medium text-[#4E6974] uppercase tracking-wide mb-3 block">What matters most?</label>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-4">
                  {[
                    { key: 'geo', label: 'Geopolitical stability' },
                    { key: 'cost', label: 'Cost competitiveness' },
                    { key: 'trade', label: 'Trade agreement favourability' },
                    { key: 'logistics', label: 'Logistics and freight reliability' },
                    { key: 'esg', label: 'ESG and compliance' },
                    { key: 'maturity', label: 'Supplier maturity and scale' },
                  ].map(({ key, label }) => (
                    <div key={key} className="space-y-1">
                      <div className="flex justify-between text-xs">
                        <span className="text-[#4E6974]">{label}</span>
                        <span className="font-semibold text-[#0D3B4F]">{weights[key][0]}</span>
                      </div>
                      <Slider value={weights[key]} onValueChange={v => setWeights(prev => ({ ...prev, [key]: v }))}
                        min={0} max={10} step={1} className="[&_[role=slider]]:bg-[#00707C]" data-testid={`slider-${key}`} />
                    </div>
                  ))}
                </div>
              </div>

              <Button onClick={generate} disabled={loading}
                className="bg-[#00707C] hover:bg-[#00707C]/90 text-white px-6" data-testid="generate-btn">
                {loading ? 'Generating...' : 'Generate alternatives'}
              </Button>
            </CardContent>
          </Card>

          {/* Results */}
          <AnimatePresence>
            {results && (
              <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.4 }}>
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-bold text-[#0D3B4F]" data-testid="results-heading">
                    Top alternatives for {results.product_label} (currently sourcing from {results.current_country_name})
                  </h2>
                  <Button onClick={handleDownloadPdf} variant="outline" size="sm"
                    className="flex items-center gap-2 text-xs border-[#0097A7] text-[#0097A7] hover:bg-[#0097A7]/10" data-testid="download-pdf-btn">
                    <Download className="h-3.5 w-3.5" /> Download brief
                  </Button>
                </div>

                <div className="space-y-3 mb-6" data-testid="alternatives-list">
                  {results.alternatives.map((alt, i) => (
                    <motion.div key={alt.country_code} initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.08 }}>
                      <Card className="bg-white rounded-lg shadow-[0_2px_8px_rgba(0,0,0,0.04)] border-none hover:shadow-[0_4px_16px_rgba(0,0,0,0.08)] transition-shadow">
                        <CardContent className="p-4">
                          <div className="flex items-start gap-4">
                            {/* Fit score */}
                            <div className="flex-shrink-0 w-16 h-16 rounded-lg bg-[#0D3B4F] flex items-center justify-center">
                              <span className="text-2xl font-bold text-white">{alt.fit_score}</span>
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1.5">
                                <h3 className="text-base font-bold text-[#0D3B4F]">{alt.country_name}</h3>
                                <Badge variant="outline" className="text-[9px] text-[#4E6974]">{alt.region?.replace('mea', 'Middle East & Africa').replace('apac', 'Asia-Pacific').replace('americas', 'Americas').replace('europe', 'Europe')}</Badge>
                              </div>
                              {/* Indicator badges */}
                              <div className="flex flex-wrap gap-2 mb-2">
                                <Badge className="text-white text-[10px]" style={{ backgroundColor: riskColor(alt.overall_risk) }}>
                                  Risk: {alt.overall_risk}
                                </Badge>
                                <Badge variant="outline" className="text-[10px] border-[#0097A7] text-[#0097A7]">{alt.trade_agreement}</Badge>
                                <Badge variant="outline" className={`text-[10px] ${alt.cost_tier === 'Lower cost' ? 'border-[#2E7D32] text-[#2E7D32]' : alt.cost_tier === 'Higher cost' ? 'border-[#FF3B30] text-[#FF3B30]' : 'border-[#FF9500] text-[#FF9500]'}`}>
                                  {alt.cost_tier}
                                </Badge>
                                <Badge variant="outline" className={`text-[10px] ${alt.logistics_tier === 'Strong' ? 'border-[#2E7D32] text-[#2E7D32]' : alt.logistics_tier === 'Challenging' ? 'border-[#FF3B30] text-[#FF3B30]' : 'border-[#FF9500] text-[#FF9500]'}`}>
                                  Logistics: {alt.logistics_tier}
                                </Badge>
                                {alt.eci_rank && (
                                  <Badge variant="outline" className="text-[10px] border-[#4E6974] text-[#4E6974]">
                                    ECI #{alt.eci_rank}
                                  </Badge>
                                )}
                              </div>
                              <p className="text-sm text-[#4E6974]">{alt.rationale}</p>
                              <div className="flex gap-3 mt-2">
                                <Link to={`/countries/${alt.country_code}`} className="text-xs text-[#0097A7] hover:underline flex items-center gap-0.5">
                                  View country detail <ChevronRight className="h-3 w-3" />
                                </Link>
                                <button onClick={() => setCompareModal(alt)} className="text-xs text-[#0097A7] hover:underline flex items-center gap-0.5" data-testid={`compare-${alt.country_code}`}>
                                  Compare with current source <ChevronRight className="h-3 w-3" />
                                </button>
                              </div>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    </motion.div>
                  ))}
                </div>

                {/* Considerations */}
                {results.considerations?.length > 0 && (
                  <Card className="bg-white rounded-lg shadow-[0_4px_24px_rgba(0,0,0,0.04)] border-none mb-6">
                    <CardHeader className="pb-2"><CardTitle className="text-base font-bold text-[#0D3B4F]">Considerations</CardTitle></CardHeader>
                    <CardContent>
                      <div className="space-y-3" data-testid="considerations">
                        {results.considerations.map((p, i) => (
                          <p key={i} className="text-sm text-[#4E6974] leading-relaxed">{p}</p>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </main>

      {/* Comparison modal */}
      <AnimatePresence>
        {compareModal && (
          <CompareModal alt={compareModal} currentCountry={currentCountry} countries={countries} onClose={() => setCompareModal(null)} />
        )}
      </AnimatePresence>
    </div>
  );
};

const CompareModal = ({ alt, currentCountry, countries, onClose }) => {
  const current = countries.find(c => c.country_code === currentCountry);
  const dims = [
    { label: 'Geopolitical stability', key: 'geopolitical' },
    { label: 'Trade policy', key: 'trade_policy' },
    { label: 'Freight & logistics', key: 'freight' },
    { label: 'Regulatory & compliance', key: 'regulatory' },
    { label: 'Labour market', key: 'labour' },
    { label: 'Currency & financial', key: 'currency' },
  ];

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-[100] flex items-center justify-center" data-testid="compare-modal">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }}
        className="relative w-full max-w-lg bg-white rounded-xl shadow-2xl overflow-hidden m-4">
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#E0E0E0]">
          <h3 className="text-base font-bold text-[#0D3B4F]">{current?.country_name || currentCountry} vs {alt.country_name}</h3>
          <button onClick={onClose} className="text-[#4E6974] hover:text-[#0D3B4F]"><X className="h-5 w-5" /></button>
        </div>
        <div className="p-5 space-y-3 max-h-[60vh] overflow-y-auto">
          {dims.map(d => {
            const altVal = alt.risk_breakdown?.[d.key] || 50;
            const curVal = current?.overall_risk_score || 50;
            return (
              <div key={d.key}>
                <div className="flex justify-between text-xs mb-1">
                  <span className="text-[#4E6974]">{d.label}</span>
                  <span className="text-[#4E6974]">{d.key === 'geopolitical' || d.key === 'trade_policy' ? 'Lower is better' : ''}</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 text-xs mb-0.5">
                      <span className="text-[#0D3B4F] font-medium w-20 truncate">{current?.country_name || '—'}</span>
                      <div className="flex-1 h-2 bg-[#E0E0E0] rounded-full overflow-hidden">
                        <div className="h-full rounded-full bg-[#0D3B4F]" style={{ width: `${curVal}%` }} />
                      </div>
                      <span className="font-semibold text-[#0D3B4F] w-6 text-right">{curVal}</span>
                    </div>
                    <div className="flex items-center gap-2 text-xs">
                      <span className="text-[#0097A7] font-medium w-20 truncate">{alt.country_name}</span>
                      <div className="flex-1 h-2 bg-[#E0E0E0] rounded-full overflow-hidden">
                        <div className="h-full rounded-full bg-[#00707C]" style={{ width: `${altVal}%` }} />
                      </div>
                      <span className="font-semibold text-[#0097A7] w-6 text-right">{altVal}</span>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        <div className="px-5 py-3 border-t border-[#E0E0E0] flex gap-2 justify-end">
          <Link to={`/countries/${alt.country_code}`}>
            <Button size="sm" className="bg-[#00707C] hover:bg-[#00707C]/90 text-xs">View {alt.country_name} detail</Button>
          </Link>
          <Button size="sm" variant="outline" onClick={onClose} className="text-xs">Close</Button>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default SourcingPage;
